import request from '@/services/request';
import type { AdditionalAddress } from '../types/clients';
import type { ClientProviderOptions } from '../utils/providerOptions';
import { Client, ClientFilters, PaginationState } from './clientTypes';

export const ClientURL = '/v1/clients/';
export interface ClientListResponse {
    results: Client[];
    count: number;
    next?: string;
    previous?: string;
}

export interface ClientCreateData {
    title?: string;
    title_other?: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    preferred_name?: string;
    type: 'Client' | 'Relationship';
    gender?: 'Male' | 'Female' | 'Other';
    gender_other?: string;
    pronouns?: 'He/Him' | 'She/Her' | 'They/Them' | 'Other';
    pronouns_other?: string;
    dob?: string;
    email: string;
    phone_number?: string;
    mobile?: string;
    plan_start_date?: string;
    plan_end_date?: string;
    service_region?: number;
    ndis_number?: string;
    client_id?: string;
    referred_by?: string;
    primary_disability?: number[];
    secondary_disabilities?: number[];
    service_type?: number[];
    aboriginal_status?: string;
    cald_status?: string;
    country_of_birth?: string;
    place_of_birth?: string;
    agreement_signed_date?: string;
    plan_current_rate?: number;
    account_file?: string;
    account_code?: string;
    additional_emails?: any;
    client_status: 'Active' | 'Inactive' | 'Pending' | 'Deceased';
    other_primary_language?: string;
    assigned_to?: number;
    cultural_requirements?: string;
    allergies?: string;
    living_arrangements?: string;
    interests?: string;
    notes?: string;
    monthly_statement_opt_out?: boolean;
    send_postal_statement?: boolean;
    all_invoices_auto_approved?: boolean;
    english_is_primary_language?: boolean;
    interpreter_required?: boolean;
    is_ndis_pace_api_access?: boolean;
    is_pace_new?: boolean;
    participant_consents_to_auditing?: boolean;
    address1?: string;
    address2?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    country?: string;
    additional_addresses?: AdditionalAddress[];
    support_coordinator?: number;
    support_coordinators?: string[];
    secondary_support_coordinators?: string[];
    plan_manager?: number;
    /** Overspending control — see Client.allow_overspending. */
    allow_overspending?: boolean;
}

export interface ClientBudgetSummary {
    client_id: string;
    client_name: string;
    total_funding: string;
    total_allocation: string;
    total_remaining: string;
    total_delivered: string;
    total_claimed: string;
    total_unclaimed: string;
    total_opening_balance: string;
    total_overspend: string;
    total_opening_deficit: string;
}

// History Types
export interface HistoryEntry {
    user_id: string | null;
    user_name: string | null;
    timestamp: string;
    changes: string | string[];
}

export const clientApi = {
    // Get clients with filters and pagination
    getClients: async (params: {
        filters?: ClientFilters;
        pagination?: Partial<PaginationState>;
        showArchived?: boolean;
        showDeactivated?: boolean;
    }): Promise<ClientListResponse> => {
        const searchParams = new URLSearchParams();

        // Add pagination
        if (params.pagination?.current) {
            searchParams.append('page', params.pagination.current.toString());
        }
        if (params.pagination?.pageSize) {
            // Backend's StandardPagination reads `page_size` (snake_case); sending the
            // camelCase `pageSize` here meant it was silently ignored, so the server
            // always fell back to its own default page size regardless of what the
            // frontend's pagination state said — causing AntD's local re-slice of the
            // mismatched response to skip whole chunks of clients between pages.
            searchParams.append('page_size', params.pagination.pageSize.toString());
        }

        // Add filters
        if (params.filters?.search) {
            searchParams.append('search', params.filters.search);
        }
        if (params.filters?.type) {
            searchParams.append('type', params.filters.type);
        }
        if (params.filters?.service_type?.length) {
            params.filters.service_type.forEach(id =>
                searchParams.append('service_type', id.toString())
            );
        }
        if (params.filters?.region) {
            searchParams.append('service_region', params.filters.region.toString());
        }
        if (params.filters?.status) {
            searchParams.append('client_status', params.filters.status);
        }
        if (params.filters?.assigned_to) {
            searchParams.append('assigned_to', params.filters.assigned_to.toString());
        }
        if (params.filters?.access_scope) {
            searchParams.append('access_scope', params.filters.access_scope);
        }
        if (params.filters?.coordinator) {
            searchParams.append('coordinator', params.filters.coordinator);
        }
        // The tier stands on its own too: with a mine/team scope it narrows
        // to that slice of the caseload even without a coordinator.
        if (params.filters?.assignment) {
            searchParams.append('assignment', params.filters.assignment);
        } else if (params.filters?.coordinator) {
            searchParams.append('assignment', 'primary');
        }

        // Add showArchived and showDeactivated parameters
        searchParams.append('showArchived', (params.showArchived || false).toString());
        searchParams.append('showDeactivated', (params.showDeactivated || false).toString());

        return await request.get(`${ClientURL}?${searchParams.toString()}`);

    },

    // Unpaginated id+name list of every client accessible to the user — for filter
    // dropdowns elsewhere in the app, where a paginated/capped result would truncate it.
    getClientDropdown: async (): Promise<{ id: string; name: string }[]> => {
        return await request.get(`${ClientURL}?dropdown=true`);
    },

    // Create a new client
    createClient: async (clientData: ClientCreateData): Promise<Client> => {
        return await request.post(ClientURL, clientData);

    },

    // Update an existing client
    updateClient: async (id: string, clientData: Partial<ClientCreateData>): Promise<Client> => {
        return await request.patch(`${ClientURL}${id}/`, clientData);

    },

    // Delete a client
    deleteClient: async (id: string): Promise<void> => {
        await request.delete(`${ClientURL}${id}/`);
    },

    // Get a single client by ID
    getClient: async (id: string): Promise<Client> => {
        return await request.get(`${ClientURL}${id}/`);

    },
    // Get aggregated budget summary for a client
    getClientBudgetSummary: async (id: string): Promise<ClientBudgetSummary> => {
        return await request.get(`${ClientURL}${id}/budget-summary/`);
    },

    // Deactivate a client
    deactivateClient: async (id: string): Promise<{ message: string; error?: string; active_contracts?: string[] }> => {
        return await request.post(`${ClientURL}${id}/deactivate/`);
    },

    // Archive a client
    archiveClient: async (id: string): Promise<{ message: string }> => {
        return await request.post(`${ClientURL}${id}/archive/`);
    },

    // Unarchive a client
    unarchiveClient: async (id: string): Promise<{ message: string }> => {
        return await request.post(`${ClientURL}${id}/unarchive/`);
    },

    // Reactivate a client
    reactivateClient: async (id: string): Promise<{ message: string }> => {
        return await request.post(`${ClientURL}${id}/reactivate/`);
    },

    getClientHistory: async (id: string): Promise<any> => {
        return await request.get(`${ClientURL}${id}/history/`);
    },

    getAssignedUsers: async (id: string): Promise<{ id: string; full_name: string; email: string }[]> => {
        return await request.get(`${ClientURL}${id}/assigned-users/`);
    },

    // Provider dropdown options for a case note: the participant's linked
    // providers (falling back to the full provider list when none are linked),
    // plus the provider on their most recently created note. The note forms
    // turn that into the default selection via defaultProviderId().
    getProviderOptions: async (id: string): Promise<ClientProviderOptions> => {
        return await request.get(`${ClientURL}${id}/provider-options/`);
    },
};

export interface ArchivedCaseNote {
    id: string;
    client_ref: string | null;
    note_title: string;
    note_category: string;
    duration: string;
    note_details: string;
    created_date: string | null;
    other: Record<string, any> | null;
    imported_by: number | null;
    imported_by_name: string | null;
    imported_at: string;
    source_file: string;
}

export interface ArchivedCaseNoteFilters {
    date_from?: string;
    date_to?: string;
    note_title?: string;
    note_category?: string;
    keyword?: string;
    page?: number;
    page_size?: number;
}

export interface PaginatedArchivedCaseNotes {
    results: ArchivedCaseNote[];
    count: number;
    next?: string | null;
    previous?: string | null;
}

export const archivedCaseNoteApi = {
    list: async (clientId: string, filters: ArchivedCaseNoteFilters = {}): Promise<PaginatedArchivedCaseNotes> => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== undefined && v !== '' && v !== null) params.append(k, String(v));
        });
        return await request.get(`${ClientURL}${clientId}/archived-case-notes/?${params.toString()}`);
    },

    import: async (clientId: string, file: File): Promise<any> => {
        const formData = new FormData();
        formData.append('file', file);
        return await request.post(`${ClientURL}${clientId}/archived-case-notes/import/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    delete: async (id: string): Promise<void> => {
        return await request.delete(`/v1/archived-case-notes/${id}/`);
    },
};
